import * as THREE from 'three/webgpu';
import { attribute, texture, vec3 } from 'three/tsl';

import { RENDER_ORDER } from './materials.js';
import { makeFalloffTexture } from './ShadowBlobs.js';
import { plateY } from '../build/prims.js';
import { streamRng } from '../math/rng.js';

/**
 * @file `createBloodPool` — the hit confirmation. A few droplets thrown off the
 * impact, gravity, and a ground splat where each one lands.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS SHAPED LIKE THIS
 * ─────────────────────────────────────────────────────────────────────────────
 * Two `InstancedMesh`es and nothing else: airborne DROPLETS and ground SPLATS.
 * Both are the same flat XZ quad the tracers use, for the same reason — at the
 * game's 52-degree pitch a ground-parallel quad reads correctly from every yaw
 * and costs no camera data, so this class constructs and steps fine in node.
 *
 * Both pools are RING BUFFERS, not free lists. A splat is decoration: when the
 * pool wraps, the oldest splat is simply overwritten, which is exactly the
 * behaviour you want under a horde (the floor never fills up, the draw never
 * grows, and no accounting can leak a slot). That is also the optimisation the
 * owner asked for — a splat is on screen for `hold + fade` seconds, ~3 s by
 * default, and then it is gone whether or not anything replaced it.
 *
 * Ground height is sampled ONCE, at splash time, and every droplet from that
 * splash lands on that plane. Sampling per droplet per frame would be the
 * "correct" thing and it buys nothing: the droplets travel under a metre.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • The colours are BELOW 1.0 on purpose. Everything else in this engine that
 *   sits in `RENDER_ORDER.emissive` is bloom fuel (tracers are 2.4); glowing
 *   blood looks like neon paint. Blood is the one bright-layer thing that must
 *   stay dark.
 * • The splat layer has an EXPLICIT FRACTIONAL renderOrder (`blobShadow + 0.5`).
 *   Two coplanar transparent layers do NOT sort by insertion order — r185 sorts
 *   by bounding-sphere Z, so a splat and a blob shadow would strobe past each
 *   other as the camera moves. Same trap the city's paint layer hit.
 * • Seeded rng (rule 4): the spray uses its own stream, so it can never perturb
 *   the population's draws or an aim roll.
 *
 * @module engine/render/Blood
 */

/** @typedef {import('../types.js').Rng} Rng */

/** Tuning, in one frozen place. */
export const BLOOD = Object.freeze({
  /** Droplets per splash, before the `strength` scale. */
  drops: 5,
  /** Metres/second the spray leaves the wound at. */
  speed: 3.4,
  /** Upward bias, m/s — enough to arc, not enough to fountain. */
  lift: 2.6,
  /** m/s², exaggerated: real gravity makes 3 m/s droplets hang far too long. */
  gravity: 22,
  /** Droplet quad edge, metres. */
  dropSize: 0.1,
  /** Splat RADIUS range, metres. */
  splatMin: 0.13,
  splatMax: 0.3,
  /** Seconds a splat holds full strength, then fades out. Short on purpose. */
  hold: 1.6,
  fade: 1.4,
  /** Metres above the sampled ground. Clears the blob shadow's 0.02 lift. */
  lift0: 0.035,
  /** Airborne droplet colour (linear, deliberately < 1 — see the header). */
  dropColor: Object.freeze([0.44, 0.035, 0.028]),
  /** Ground splat colour: darker, it has soaked in. */
  splatColor: Object.freeze([0.21, 0.018, 0.014]),
  splatOpacity: 0.85,
});

/**
 * @param {THREE.Object3D} parent Identity-transform parent (level root).
 * @param {Object} [o]
 * @param {number} [o.drops=96] Airborne droplet slots.
 * @param {number} [o.splats=64] Ground splat slots. See the ring-buffer note.
 * @param {(x: number, z: number) => number} [o.heightAt] Ground sampler.
 * @param {Rng|string|number} [o.rng='blood'] A stream, or a seed for one.
 * @param {typeof BLOOD} [o.tune]
 * @returns {{droplets: THREE.InstancedMesh, splats: THREE.InstancedMesh,
 *   splash: Function, update: Function, stats: Function, dispose: Function}}
 */
export function createBloodPool(parent, o = {}) {
  const {
    drops: maxDrops = 96,
    splats: maxSplats = 64,
    heightAt = () => 0,
    rng: rngOpt = 'blood',
    tune = BLOOD,
  } = o;
  const T = tune === BLOOD ? BLOOD : { ...BLOOD, ...tune };
  const rng = typeof rngOpt === 'function' ? rngOpt : streamRng(rngOpt, 'blood');

  // ── droplets ───────────────────────────────────────────────────────────────
  const dropGeo = plateY(1, 1);
  const dropAlpha = new THREE.InstancedBufferAttribute(new Float32Array(maxDrops), 1);
  dropAlpha.setUsage(THREE.DynamicDrawUsage);
  dropGeo.setAttribute('iAlpha', dropAlpha);
  dropGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

  const dropMat = new THREE.MeshBasicNodeMaterial();
  dropMat.name = 'bloodDrop';
  dropMat.transparent = true;
  dropMat.depthWrite = false;
  dropMat.fog = false;
  dropMat.vertexColors = false;
  dropMat.side = THREE.DoubleSide;
  dropMat.colorNode = vec3(T.dropColor[0], T.dropColor[1], T.dropColor[2]);
  dropMat.opacityNode = attribute('iAlpha', 'float');

  const droplets = new THREE.InstancedMesh(dropGeo, dropMat, maxDrops);
  droplets.name = 'bloodDroplets';
  droplets.count = maxDrops;
  droplets.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  droplets.instanceMatrix.array.fill(0);
  // Above the splats and the shadows; the tracers share this order and never
  // overlap a droplet for long enough for the tie to matter.
  droplets.renderOrder = RENDER_ORDER.emissive;
  droplets.frustumCulled = false;
  droplets.matrixAutoUpdate = false;
  droplets.updateMatrix();
  droplets.visible = false;
  parent.add(droplets);

  // ── ground splats ──────────────────────────────────────────────────────────
  const splatTex = makeFalloffTexture(32, 0.25);
  splatTex.name = 'bloodFalloff';
  const splatGeo = plateY(1, 1);
  const splatAlpha = new THREE.InstancedBufferAttribute(new Float32Array(maxSplats), 1);
  splatAlpha.setUsage(THREE.DynamicDrawUsage);
  splatGeo.setAttribute('iAlpha', splatAlpha);
  splatGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

  const splatMat = new THREE.MeshBasicNodeMaterial();
  splatMat.name = 'bloodSplat';
  splatMat.transparent = true;
  splatMat.depthWrite = false;
  splatMat.fog = false;
  splatMat.vertexColors = false;
  splatMat.side = THREE.DoubleSide;
  splatMat.colorNode = vec3(T.splatColor[0], T.splatColor[1], T.splatColor[2]);
  // The falloff texture carries the blob shape in ALPHA only (see ShadowBlobs),
  // and it has to ride `opacityNode` — assigning `.map` / `.alphaMap` does
  // NOTHING once `colorNode` / `opacityNode` are set, because a NodeMaterial
  // only synthesises those from the legacy slots when the node is null.
  splatMat.opacityNode = texture(splatTex).a.mul(attribute('iAlpha', 'float'));

  const splats = new THREE.InstancedMesh(splatGeo, splatMat, maxSplats);
  splats.name = 'bloodSplats';
  splats.count = maxSplats;
  splats.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  splats.instanceMatrix.array.fill(0);
  splats.renderOrder = RENDER_ORDER.blobShadow + 0.5;
  splats.frustumCulled = false;
  splats.matrixAutoUpdate = false;
  splats.updateMatrix();
  splats.visible = false;
  parent.add(splats);

  // ── droplet state ──────────────────────────────────────────────────────────
  const dx = new Float32Array(maxDrops);
  const dy = new Float32Array(maxDrops);
  const dz = new Float32Array(maxDrops);
  const dvx = new Float32Array(maxDrops);
  const dvy = new Float32Array(maxDrops);
  const dvz = new Float32Array(maxDrops);
  /** Ground plane this droplet lands on (sampled once at splash time). */
  const dgy = new Float32Array(maxDrops);
  /** Splat radius this droplet will leave. */
  const dr = new Float32Array(maxDrops);
  /** 1 = airborne, 0 = idle. */
  const dLive = new Int8Array(maxDrops);
  let dropCursor = 0;
  let dropsLive = 0;

  // ── splat state ────────────────────────────────────────────────────────────
  /** Seconds since it landed; < 0 = idle. */
  const sAge = new Float32Array(maxSplats).fill(-1);
  let splatCursor = 0;
  let splatsLive = 0;

  let disposed = false;

  /** @param {number} i */
  const clearDrop = (i) => {
    const m = droplets.instanceMatrix.array;
    const o = i * 16;
    for (let k = 0; k < 16; k++) m[o + k] = 0;
    dropAlpha.array[i] = 0;
  };

  /** @param {number} i */
  const writeDrop = (i) => {
    const m = droplets.instanceMatrix.array;
    const o = i * 16;
    const s = T.dropSize;
    m[o] = s; m[o + 1] = 0; m[o + 2] = 0; m[o + 3] = 0;
    m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
    m[o + 8] = 0; m[o + 9] = 0; m[o + 10] = s; m[o + 11] = 0;
    m[o + 12] = dx[i]; m[o + 13] = dy[i]; m[o + 14] = dz[i]; m[o + 15] = 1;
    dropAlpha.array[i] = 1;
  };

  /**
   * Lands a droplet: stamps a splat at its resting place and frees the slot.
   * @param {number} i Droplet slot.
   */
  const land = (i) => {
    const j = splatCursor;
    splatCursor = (splatCursor + 1) % maxSplats;
    if (sAge[j] < 0) splatsLive++;
    const d = dr[i] * 2;
    const m = splats.instanceMatrix.array;
    const o = j * 16;
    m[o] = d; m[o + 1] = 0; m[o + 2] = 0; m[o + 3] = 0;
    m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
    m[o + 8] = 0; m[o + 9] = 0; m[o + 10] = d; m[o + 11] = 0;
    m[o + 12] = dx[i];
    m[o + 13] = dgy[i] + T.lift0;
    m[o + 14] = dz[i];
    m[o + 15] = 1;
    sAge[j] = 0;
    splatAlpha.array[j] = T.splatOpacity;
    dLive[i] = 0;
    clearDrop(i);
  };

  return {
    droplets,
    splats,

    /**
     * Throws a spray from a hit. Call it ONLY on a shot that connected — a
     * miss that bleeds is worse feedback than no blood at all.
     *
     * @param {number} x @param {number} y Impact height (chest, not feet).
     *   @param {number} z
     * @param {number} nx @param {number} nz Shot direction, shooter -> victim.
     *   Need not be normalised; the spray is biased along it (exit side) with a
     *   wide cone, so a hit visibly throws blood the way the bullet went.
     * @param {number} [strength=1] Scales droplet COUNT and speed. Use it for
     *   big hits; the default is one auto-attack tick.
     */
    splash(x, y, z, nx, nz, strength = 1) {
      if (disposed) return;
      const l = Math.hypot(nx, nz);
      const ux = l > 1e-6 ? nx / l : 0;
      const uz = l > 1e-6 ? nz / l : 1;
      const n = Math.max(1, Math.round(T.drops * (strength > 0 ? strength : 1)));
      for (let k = 0; k < n; k++) {
        const i = dropCursor;
        dropCursor = (dropCursor + 1) % maxDrops;
        if (!dLive[i]) dropsLive++;
        // Cone around the shot direction: +-60 degrees, so the spray fans out
        // instead of forming a line the eye reads as one object.
        const ang = (rng() * 2 - 1) * 1.05;
        const c = Math.cos(ang);
        const s = Math.sin(ang);
        const sp = T.speed * (0.55 + 0.75 * rng()) * (strength > 0 ? Math.sqrt(strength) : 1);
        dx[i] = x;
        dy[i] = y;
        dz[i] = z;
        dvx[i] = (ux * c - uz * s) * sp;
        dvz[i] = (ux * s + uz * c) * sp;
        dvy[i] = T.lift * (0.4 + 0.9 * rng());
        dgy[i] = heightAt(x, z);
        dr[i] = T.splatMin + (T.splatMax - T.splatMin) * rng();
        dLive[i] = 1;
        writeDrop(i);
      }
    },

    /** @param {number} dt */
    update(dt) {
      if (disposed) return;

      // ── droplets: ballistic, land on their sampled plane ───────────────────
      if (dropsLive > 0) {
        dropsLive = 0;
        for (let i = 0; i < maxDrops; i++) {
          if (!dLive[i]) continue;
          dvy[i] -= T.gravity * dt;
          dx[i] += dvx[i] * dt;
          dy[i] += dvy[i] * dt;
          dz[i] += dvz[i] * dt;
          if (dy[i] <= dgy[i] + T.lift0) {
            land(i);
            continue;
          }
          writeDrop(i);
          dropsLive++;
        }
        droplets.instanceMatrix.needsUpdate = true;
        dropAlpha.needsUpdate = true;
        // `> 0` and not `true`: the last live droplet lands INSIDE this loop, so
        // flipping unconditionally would draw one dead frame of nothing.
        droplets.visible = dropsLive > 0;
      } else if (droplets.visible) {
        droplets.visible = false;
      }

      // ── splats: hold, then fade, then the slot is idle ─────────────────────
      if (splatsLive > 0) {
        splatsLive = 0;
        for (let j = 0; j < maxSplats; j++) {
          if (sAge[j] < 0) continue;
          const a = (sAge[j] += dt);
          if (a >= T.hold + T.fade) {
            sAge[j] = -1;
            splatAlpha.array[j] = 0;
            const m = splats.instanceMatrix.array;
            const o = j * 16;
            for (let k = 0; k < 16; k++) m[o + k] = 0;
            continue;
          }
          const f = a <= T.hold ? 1 : 1 - (a - T.hold) / T.fade;
          splatAlpha.array[j] = T.splatOpacity * f;
          splatsLive++;
        }
        splats.instanceMatrix.needsUpdate = true;
        splatAlpha.needsUpdate = true;
        splats.visible = splatsLive > 0;
      } else if (splats.visible) {
        splats.visible = false;
      }
    },

    /** @returns {{drops: number, splats: number, maxDrops: number, maxSplats: number}} */
    stats() {
      return { drops: dropsLive, splats: splatsLive, maxDrops, maxSplats };
    },

    /** Idempotent. */
    dispose() {
      if (disposed) return;
      disposed = true;
      droplets.removeFromParent();
      droplets.dispose();
      splats.removeFromParent();
      splats.dispose();
      dropGeo.dispose();
      splatGeo.dispose();
      dropMat.dispose();
      splatMat.dispose();
      splatTex.dispose();
    },
  };
}
